// client/src/pages/ProfilePage.js
import React, { useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Box, Button, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import SideNav from '../components/SideNav';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import MyOrders from './MyOrders';
import AccountSettings from './AccountSettings';
import BecomeSeller from './BecomeSeller';
import MyProducts from './MyProducts';
import WishlistPage from './WishlistPage';
import MyModelsPage from './MyModelsPage';
import SellerInventory from './SellerInventory';
import SellerOrders from './SellerOrders';
import DeliveryPartners from './DeliveryPartners';
import AddressesPage from './AddressesPage';
import MessagesPage from './MessagesPage';
import SellerAnalytics from './SellerAnalytics';
import SellerStorefront from './SellerStorefront';

// Each section has its own URL (/profile?view=orders), so links, refresh, and
// the browser's back button all land on the right one.
const viewPath = (view) => (view === 'dashboard' ? '/profile' : `/profile?view=${view}`);

const ProfileDashboard = ({ user, userProfile, isEnabled }) => {
  const shortcuts = [
    { view: 'orders', title: 'My Orders', text: 'Track deliveries and view past orders.', icon: ShoppingBagOutlinedIcon },
    { view: 'my-models', title: 'My Models', text: 'Find parts that fit what you fly.', icon: FlightOutlinedIcon },
    { view: 'addresses', title: 'Addresses', text: 'Save addresses for faster checkout.', icon: HomeOutlinedIcon },
    ...(isEnabled('wishlist') ? [{ view: 'wishlist', title: 'Wishlist', text: 'Products you saved for later.', icon: FavoriteBorderIcon }] : []),
    { view: 'settings', title: 'Account Settings', text: 'Update your details and password.', icon: SettingsOutlinedIcon },
  ];

  return (
    <Box>
      <Typography variant="overline" color="primary" sx={{ display: 'block' }}>Your account</Typography>
      <Typography variant="h4" sx={{ overflowWrap: 'anywhere', mb: 1.5 }}>Welcome back, {user.email}</Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {user.role !== 'customer' && <Chip label={user.role.replace('_', ' ')} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />}
        {userProfile?.client_number && <Chip label={`Client no. ${userProfile.client_number}`} size="small" variant="outlined" />}
        {userProfile?.loyalty_points > 0 && (
          <Chip label={`${userProfile.loyalty_points} loyalty points to redeem at checkout`} size="small" color="secondary" variant="outlined" />
        )}
      </Stack>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
        {shortcuts.map(({ view, title, text, icon: Icon }) => (
          <Box
            key={view}
            component={Link}
            to={viewPath(view)}
            sx={{
              p: 2.5, border: 1, borderColor: 'divider', color: 'text.primary', textDecoration: 'none',
              transition: 'border-color 0.2s ease, transform 0.2s ease',
              '&:hover .shortcut-arrow': { transform: 'translateX(4px)' },
              '&:hover, &:focus-visible': { borderColor: 'primary.main' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5, color: 'primary.main' }}>
              <Icon />
              <ArrowForwardIcon className="shortcut-arrow" fontSize="small" sx={{ color: 'text.secondary', transition: 'transform 0.2s ease' }} />
            </Stack>
            <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{text}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ProfilePage = () => {
  const { user, userProfile } = useContext(AuthContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const [searchParams] = useSearchParams();
  const activeView = searchParams.get('view') || 'dashboard';

  const canSell = user?.role === 'seller'; // Admins manage everything in the admin panel.
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  const item = (view, label, icon) => ({ key: view, label, icon, to: viewPath(view), selected: activeView === view });
  const groups = [
    {
      items: [
        item('dashboard', 'Dashboard', DashboardOutlinedIcon),
        item('orders', 'My Orders', ShoppingBagOutlinedIcon),
        item('my-models', 'My Models', FlightOutlinedIcon),
        item('addresses', 'Addresses', HomeOutlinedIcon),
        ...(isEnabled('wishlist') ? [item('wishlist', 'Wishlist', FavoriteBorderIcon)] : []),
        ...(isEnabled('live_chat') ? [item('messages', 'Messages', ChatBubbleOutlineIcon)] : []),
        item('settings', 'Account Settings', SettingsOutlinedIcon),
        ...(isEnabled('become_seller') && user?.role === 'customer' ? [item('become-seller', 'Become a Seller', SellOutlinedIcon)] : []),
      ],
    },
    ...(canSell ? [{
      title: 'Selling',
      items: [
        item('my-products', 'My Products', Inventory2OutlinedIcon),
        item('seller-inventory', 'Inventory', WarehouseOutlinedIcon),
        item('seller-orders', 'Orders to Fulfill', ReceiptLongOutlinedIcon),
        item('seller-analytics', 'Analytics', AnalyticsOutlinedIcon),
        item('seller-storefront', 'Storefront', StorefrontOutlinedIcon),
        item('delivery-partners', 'Delivery Partners', LocalShippingOutlinedIcon),
      ],
    }] : []),
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'orders': return <MyOrders />;
      case 'my-models': return <MyModelsPage />;
      case 'settings': return <AccountSettings />;
      case 'addresses': return <AddressesPage />;
      case 'wishlist': return <WishlistPage />;
      case 'messages': return <MessagesPage />;
      case 'my-products': return <MyProducts />;
      case 'become-seller': return <BecomeSeller />;
      case 'seller-inventory': return <SellerInventory />;
      case 'seller-orders': return <SellerOrders />;
      case 'seller-analytics': return <SellerAnalytics />;
      case 'seller-storefront': return <SellerStorefront />;
      case 'delivery-partners': return <DeliveryPartners />;
      default:
        if (!user) return <Typography>Loading your account...</Typography>;
        return <ProfileDashboard user={user} userProfile={userProfile} isEnabled={isEnabled} />;
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ display: 'grid', gap: { xs: 2, md: 4 }, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '230px minmax(0, 1fr)' }, alignItems: 'start' }}>
        <SideNav
          title="My Account"
          groups={groups}
          footer={isAdmin && (
            <Button component={Link} to="/admin" size="small" variant="outlined" startIcon={<AdminPanelSettingsOutlinedIcon />} sx={{ ml: 0.5 }}>
              Admin Panel
            </Button>
          )}
        />
        <Box sx={{ minWidth: 0 }}>
          {isAdmin && (
            <Button component={Link} to="/admin" size="small" variant="outlined" startIcon={<AdminPanelSettingsOutlinedIcon />} sx={{ display: { xs: 'inline-flex', md: 'none' }, mb: 1.5 }}>
              Admin Panel
            </Button>
          )}
          <Paper sx={{ p: { xs: 2, md: 3.5 }, minHeight: 420 }}>{renderContent()}</Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default ProfilePage;
